/**
 * useEstatePhotos — an estate page's Google Places photos, fetched PER VIEW
 * from the browser (D14).
 *
 * Google's terms permit storing a place id and nothing else of a photo, so the
 * photos are neither stored nor server-rendered: after hydration this query
 * asks the BFF (`/api/estates/photos` → `EstateService.ListEstatePhotos`),
 * which has the backend list the place's photos and mint each serving URI for
 * this view. React Query never runs a `queryFn` during SSR, so the
 * server-rendered HTML carries no Google URL.
 *
 * Every fetch is BILLED (one Place Photo call per photo), hence:
 * - `retry: false` — a retried fetch pays again for photos the first attempt
 *   may already have minted; a failed gallery is simply omitted;
 * - no refetch on focus/reconnect, and a long `staleTime` — the URIs stay in
 *   this tab's in-memory cache for the session (the browser keeps the bytes
 *   for as long as Google's own `Cache-Control: private` allows).
 *
 * A failure is logged and counted server-side by the BFF route and by the
 * backend's `google_api_requests_total{status}`; the page just omits the
 * gallery.
 */
import { bff } from '@/shared/lib/bff';
import { queryKeys } from '@/shared/lib/query-keys';
import type { EstatePhotoData } from '@/shared/lib/types/estates';
import { bffError } from '@letsgethome/lib/server-errors';
import type { PublicId } from '@letsgethome/types/branded';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

/** How long a view's photo URIs are reused in this tab before a re-mint. */
const ESTATE_PHOTOS_STALE_MS = 30 * 60_000;

/**
 * `enabled: false` holds the fetch — `EstateGallery` passes whether the
 * gallery has come into view, so a page whose gallery is never scrolled to
 * mints (and bills) nothing.
 */
export function useEstatePhotos(
  estatePublicId: PublicId,
  enabled = true
): UseQueryResult<EstatePhotoData[]> {
  return useQuery({
    queryKey: queryKeys.estates.photos(estatePublicId),
    enabled,
    queryFn: async () => {
      const result = await bff('/api/estates/photos', { estatePublicId });
      if (!result.success) {
        throw bffError(result.error, 'Estate photos failed');
      }
      return result.data?.photos ?? [];
    },
    retry: false,
    staleTime: ESTATE_PHOTOS_STALE_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
